/**
 * Chain detection from address format. Mirror of backend
 * `app/analysis/address_utils.py::detect_chain_candidates()`.
 *
 * `detectChainCandidates(addr)` returns ALL plausible chains for an address.
 * Single-element for unambiguous formats (TRX, BTC, DOGE, LTC, SOL, XRP, TON,
 * Cardano, Cosmos). Multi-element for the EVM family — those addresses share
 * one hex format and the only way to know which chain holds value is to probe
 * RPCs in parallel via the chain_probe service.
 *
 * `detectChain(addr)` is the back-compat single-string shim that returns the
 * first candidate (or 'ETH' as a fallback default for legacy callers that
 * cannot handle null). For new code prefer `detectChainStrict` (returns null
 * on unrecognised input) or `detectChainCandidates` (returns the full list).
 */
import { identifyBase58Chain } from './base58check';

// Order matters — ETH stays first as the default for back-compat
export const EVM_CHAINS_ORDERED = [
    'ETH', 'BSC', 'OPBNB', 'MATIC', 'ARB', 'BASE', 'OP', 'AVAX',
    'FTM', 'LINEA', 'SCROLL', 'ZKSYNC', 'BLAST',
    'GNOSIS', 'CELO', 'MANTLE', 'MOONBEAM',
] as const;

const RE_EVM = /^0x[0-9a-fA-F]{40}$/;

/** Left-pad truncated 0x hex to 40 digits (CSV/Excel often drops zeros). */
export function coerceEvmAddress(addr: string): string {
    const s = (addr || '').trim();
    if (s.length < 3 || !/^0x/i.test(s)) return s;
    const body = s.slice(2);
    if (!body || !/^[0-9a-fA-F]+$/.test(body) || body.length > 40) return s;
    if (body.length >= 37 && body.length < 40) return '0x' + body.padStart(40, '0');
    return '0x' + body;
}

const RE_BASE58 = /^[1-9A-HJ-NP-Za-km-z]+$/;
const RE_BECH32_BTC = /^bc1[023456789acdefghjklmnpqrstuvwxyz]{8,87}$/;
const RE_BECH32_LTC = /^ltc1[023456789acdefghjklmnpqrstuvwxyz]{8,87}$/;
const RE_BCH_CASHADDR = /^(bitcoincash:)?[qp][023456789acdefghjklmnpqrstuvwxyz]{41}$/;
const RE_TON_RAW = /^-?[01]:[a-fA-F0-9]{64}$/;
const RE_TON_USER = /^(EQ|UQ|kQ|0Q)[A-Za-z0-9_-]{46}$/;
const RE_CARDANO = /^addr1[02-9ac-hj-np-z]{50,}$/;
const RE_COSMOS = /^(cosmos|osmo|juno|stars)1[0-9a-z]{38}$/;
const RE_TRON_HEX = /^41[0-9a-f]{40}$/i;
// Stellar (XLM) base32 StrKey — must be tested BEFORE RE_BASE58 since Bitcoin
// alphabet is a superset and would match XLM addresses as SOL otherwise.
const RE_STELLAR = /^G[A-Z2-7]{55}$/;
// Zcash sapling/unified (bech32-like, lowercase)
const RE_ZEC_SAPLING = /^zs1[0-9a-z]{75,}$/;
const RE_ZEC_UNIFIED = /^u1[0-9a-z]{75,}$/;
// Monero: standard address (starts with 4, 95 chars), subaddress (starts with 8, 95 chars),
// integrated address (starts with 4, 106 chars). All use Bitcoin base58 alphabet.
// Must be checked before the generic base58 block to avoid SOL/BTC misclassification.
const RE_XMR_ADDRESS = /^4[1-9A-HJ-NP-Za-km-z]{94}$/;
const RE_XMR_SUBADDRESS = /^8[1-9A-HJ-NP-Za-km-z]{94}$/;
const RE_XMR_INTEGRATED = /^4[1-9A-HJ-NP-Za-km-z]{105}$/;

/**
 * Return all chains an address could plausibly belong to. Empty array on
 * unrecognised formats.
 */
export function detectChainCandidates(address: string | null | undefined): string[] {
    if (!address) return [];
    const s = address.trim();
    if (!s) return [];
    const evm = coerceEvmAddress(s);
    const sl = evm.toLowerCase();

    // 1. EVM hex — ambiguous family, return all candidates
    if (RE_EVM.test(evm)) return [...EVM_CHAINS_ORDERED];

    // Tron hex format (41-prefixed, 42 hex chars) — pre-base58 conversion
    if (RE_TRON_HEX.test(s)) return ['TRX'];

    // 2. Bech32 / cashaddr (deterministic by HRP)
    if (RE_BECH32_BTC.test(sl)) return ['BTC'];
    if (RE_BECH32_LTC.test(sl)) return ['LTC'];
    if (RE_BCH_CASHADDR.test(sl)) return ['BCH'];

    // 3. Zcash sapling/unified (bech32-like, check before base58)
    if (RE_ZEC_SAPLING.test(sl) || RE_ZEC_UNIFIED.test(sl)) return ['ZEC'];

    // 4. TON
    if (RE_TON_RAW.test(s) || RE_TON_USER.test(s)) return ['TON'];

    // 5. Cardano shelley
    if (RE_CARDANO.test(sl)) return ['ADA'];

    // 6. Cosmos-family
    if (RE_COSMOS.test(sl)) return ['COSMOS'];

    // 7. Stellar (XLM) base32 — must be before base58 block
    if (RE_STELLAR.test(s)) return ['XLM'];

    // 7b. Monero — must be before base58 block (same alphabet, would fall to SOL)
    if (RE_XMR_ADDRESS.test(s) || RE_XMR_SUBADDRESS.test(s) || RE_XMR_INTEGRATED.test(s)) return ['XMR'];

    // 8. Base58 family.
    if (RE_BASE58.test(s)) {
        // 8a. Checksum-confirmed identification (definitive). For base58check
        // chains (BTC/LTC/DOGE/DASH/FIRO/TRX/XRP/ZEC-transparent) the version
        // byte names the chain and the 4-byte sha256d trailer proves the string
        // is a real address; for SOL the 32-byte ed25519 curve check confirms a
        // public key. This is ASSISTIVE: it only ever returns a verified chain,
        // so when it can't confirm one (synthetic/typo'd address, off-curve PDA,
        // unknown version byte) it returns [] and we fall through to the
        // prefix/length heuristic below — a valid address can never regress to
        // "unrecognised" because of this branch. See base58check.ts.
        const confirmed = identifyBase58Chain(s);
        if (confirmed.length > 0) return confirmed;

        const n = s.length;
        const c0 = s[0];

        // TRON: T-prefix, exactly 34 chars
        if (c0 === 'T' && n === 34) return ['TRX'];
        // Dogecoin: D, A, or 9 prefix, 34 chars
        if ((c0 === 'D' || c0 === 'A' || c0 === '9') && n === 34) return ['DOGE'];
        // Litecoin: L (P2PKH) or M (P2SH), 34 chars
        if ((c0 === 'L' || c0 === 'M') && n === 34) return ['LTC'];
        // DASH: X-prefix P2PKH, exactly 34 chars
        // (DASH P2SH starts with '7' — falls to SOL below; document the gap)
        if (c0 === 'X' && n === 34) return ['DASH'];
        // Zcash transparent: t1 (P2PKH) or t3 (P2SH), exactly 35 chars
        if ((s.startsWith('t1') || s.startsWith('t3')) && n === 35) return ['ZEC'];
        // Firo (ex-Zcoin): a-prefix P2PKH, exactly 34 chars
        if (c0 === 'a' && n === 34) return ['FIRO'];
        // Bitcoin legacy: 1 (P2PKH) or 3 (P2SH), 26-35 chars
        if ((c0 === '1' || c0 === '3') && n >= 26 && n <= 35) return ['BTC'];
        // XRP classic
        if (c0 === 'r' && n >= 25 && n <= 35) return ['XRP'];
        // Solana fallback: 32-44 base58 chars
        if (n >= 32 && n <= 44) return ['SOL'];
    }

    return [];
}

/** True if the address could plausibly belong to >1 chain (i.e., EVM family). */
export function isAmbiguousChain(address: string | null | undefined): boolean {
    return detectChainCandidates(address).length > 1;
}

/**
 * Strict detector: returns the first candidate, or null if unrecognised.
 * Use this in new code where you can handle null gracefully.
 */
export function detectChainStrict(address: string | null | undefined): string | null {
    const cands = detectChainCandidates(address);
    return cands.length > 0 ? cands[0] : null;
}

/**
 * Back-compat detector: returns first candidate, or 'ETH' as a fallback default
 * (matching the prior behavior of the inline `detectChain` in Workbench.tsx).
 *
 * NOTE: this fallback is wrong for unrecognised input but is kept for now
 * because ~22 callsites in Workbench.tsx assume a non-null string. Migrate
 * those to `detectChainStrict` over time.
 */
export function detectChain(address: string | null | undefined): string {
    return detectChainStrict(address) ?? 'ETH';
}
